import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  styled,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { HighQuality, SettingsOutlined, FourK, Hd, TwoK } from '@mui/icons-material';
import axiosInstance from '../api/axiosConfig';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '50px', // Match inner button rounded corners
  gap: 4,
  '& .MuiToggleButton-root': {
    border: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    padding: '4px 8px', // Reduced padding
    textTransform: 'none',
    borderRadius: '50px', // Round corners
    minWidth: '60px', // Decreased width
    fontSize: '0.7rem', // Smaller font size
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
}));

const VideoSettings = () => {
  const [videoConfig, setVideoConfig] = useState({ resolution: '', availableResolutions: [] });
  const [message, setMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchVideoConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVideoConfig = async () => {
    try {
      const response = await axiosInstance.get(`${backendUrl}/api/config/video`);
      setVideoConfig(response.data);
    } catch (error) {
      console.error('Error fetching video config:', error);
      setMessage('Failed to load video settings');
    }
  };

  const handleResolutionChange = async (_: React.MouseEvent<HTMLElement>, newResolution: string | null) => {
    if (!newResolution) return;
    
    try {
      const response = await axiosInstance.patch(`${backendUrl}/api/config/video/resolution`, {
        resolution: newResolution,
      });
      setVideoConfig(response.data);
      setMessage('Video settings updated successfully');
    } catch (error) {
      console.error('Error updating resolution:', error);
      setMessage('Failed to update video settings');
    }
  };

  const getIcon = (resolution: string) => {
    switch (resolution) {
      case '720p':
        return <HighQuality fontSize='small' />;
      case '1080p':
        return <Hd fontSize='small' />;
      case '2K':
        return <TwoK fontSize='small' />;
      case 'no-limit':
        return <FourK fontSize='small' />;
      default:
        return null;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (resolution: string) => {
    handleResolutionChange({} as React.MouseEvent<HTMLElement>, resolution);
    handleMenuClose();
  };

  if (isMobile) {
    return (
      <>
        <Tooltip title="Video Quality">
          <IconButton
            onClick={handleMenuClick}
            size="small"
            sx={{ 
              color: 'inherit',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            {getIcon(videoConfig.resolution) || <SettingsOutlined />}
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {videoConfig.availableResolutions.map((res) => (
            <MenuItem
              key={res}
              onClick={() => handleMenuItemClick(res)}
              selected={res === videoConfig.resolution}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {getIcon(res)}
              <Typography>
                {res === 'no-limit' ? 'Native' : res}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
        <Snackbar
          open={!!message}
          autoHideDuration={6000}
          onClose={() => setMessage('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setMessage('')} severity="info" sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <StyledToggleButtonGroup
        value={videoConfig.resolution}
        exclusive
        onChange={handleResolutionChange}
        aria-label="video resolution"
        size="small"
      >
        {videoConfig.availableResolutions.map((res) => (
          <ToggleButton key={res} value={res}>
            {getIcon(res)}
            <Typography sx={{ ml: 1 }}>
              {res === 'no-limit' ? '4K' : res}
            </Typography>
          </ToggleButton>
        ))}
      </StyledToggleButtonGroup>
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setMessage('')} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoSettings;